import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { Stack, Typography, Box, TextField, Dialog, DialogContent, CircularProgress, Autocomplete, Grid } from '@mui/material';
import axios from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';
import ProductUOMForm from './ProductUOMForm';
import ProductPictureUploader from './ProductPictureUploader';
import ConfirmDialog from '../../reuseable-components/ConfirmDialog/ConfirmDialog';
import updateItemInPlaceOrAppend from '../../utils/updateItemInPlaceOrAppend';

ProductForm.propTypes = {
  editProduct: PropTypes.object,
  setProducts: PropTypes.func,
  vendors: PropTypes.array,
  categories: PropTypes.array,
  uomTypes: PropTypes.array,
  open: PropTypes.bool,
  close: PropTypes.func
}

export default function ProductForm({ editProduct, setProducts, vendors, categories, uomTypes, open, close }) {
  const { enqueueSnackbar } = useSnackbar();

  const [submitting, setSubmitting] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [formValue, setFormValue] = useState({});
  const [orderGuidesToUpdate, setOrderGuidesToUpdate] = useState([]);

  useEffect(() => {
    if (editProduct) {
      setLoadingProduct(true);
      axios.get(`/product/product?id=${editProduct.id}`)
        .then(res => setFormValue(res.data ?? {}))
        .catch(() => enqueueSnackbar('Get product data failed', { variant: 'error' }))
        .finally(() => setLoadingProduct(false));
    } else {
      setFormValue({ uomTypes: [{}] });
      setOrderGuidesToUpdate([]);
      setLoadingProduct(false)
    }
  }, [open]);

  const onSubmit = () => {
    setSubmitting(true);
    axios.post("/product/productForm", objectToFormData(formValue))
      .then((res) => {
        if (res.data) {
          enqueueSnackbar(`Product ${(editProduct ? "Updated" : "Created")}`, { variant: 'success' });
          res.data.vendor = vendors.find(vendor => vendor.value === res.data.vendorId)?.label;
          res.data.category = categories.find(category => category.value === res.data.productCategoryId)?.label;
          setProducts(products => updateItemInPlaceOrAppend(products, res.data));
          if (res.data.orderGuidesToUpdate && res.data.orderGuidesToUpdate.length) {
            setOrderGuidesToUpdate(res.data.orderGuidesToUpdate);
          } else close();
        }
        else enqueueSnackbar(`${(editProduct ? "Update" : "Create")} product failed`, { variant: 'error' })
      })
      .catch(() => enqueueSnackbar(`${(editProduct ? "Update" : "Create")} product failed`, { variant: 'error' }))
      .finally(() => setSubmitting(false));
  };

  return <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
    <DialogContent>
      {!loadingProduct ? <Stack spacing={1} minWidth="600px">
        <Box>
          <Typography variant="subtitle1">Vendor*</Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={vendors.find(vendor => vendor.value === formValue.vendorId) ?? null}
            onChange={(event, val) => setFormValue(form => ({ ...form, vendorId: val?.value ?? 0 }))}
            options={vendors}
            renderInput={(params) => <TextField {...params} placeholder="Vendor" />}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Product Name*</Typography>
          <TextField
            name="name"
            size="small"
            placeholder="Enter Product Name"
            fullWidth
            value={formValue.name ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, name: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Vendor Item Id (SKU)*</Typography>
          <TextField
            name="vendorItemId"
            size="small"
            placeholder="Enter Vendor Item Id"
            fullWidth
            value={formValue.vendorItemId ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, vendorItemId: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Category</Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={categories.find(category => category.value === formValue.productCategoryId) ?? null}
            onChange={(event, val) => setFormValue(form => ({ ...form, productCategoryId: val?.value ?? 0 }))}
            options={categories}
            renderInput={(params) => <TextField {...params} placeholder="Category" />}
          />
        </Box>
        <Box>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="subtitle1">Manufacturer</Typography>
              <TextField
                name="manufacturer"
                size="small"
                placeholder="Enter manufacturer"
                fullWidth
                value={formValue.manufacturer ?? ""}
                onChange={(e) => setFormValue(form => ({ ...form, manufacturer: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1">Manufacturer SKU</Typography>
              <TextField
                name="manufacturerId"
                size="small"
                placeholder="Enter manufacturer SKU"
                fullWidth
                value={formValue.manufacturerId ?? ""}
                onChange={(e) => setFormValue(form => ({ ...form, manufacturerId: e.target.value }))}
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <Typography variant="subtitle1">Manufacturer Link</Typography>
          <TextField
            name="manufacturerLink"
            size="small"
            placeholder="Enter Manufacturer Link"
            fullWidth
            value={formValue.manufacturerLink ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, manufacturerLink: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Description</Typography>
          <TextField
            rows={3}
            multiline
            name="description"
            size="small"
            placeholder="Enter description"
            fullWidth
            value={formValue.description ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, description: e.target.value }))}
          />
        </Box>       
        <Box />
        <ProductUOMForm formValue={formValue} defaultUOMTypes={uomTypes} setFormValue={setFormValue} />
        <ProductPictureUploader formValue={formValue} setFormValue={setFormValue} />
        <Box>
          <LoadingButton
            loading={submitting} disabled={!formValue?.name || !formValue.vendorId || !formValue.vendorItemId} sx={{ display: "flex", justifySelf: "center", mb: 2, px: 7 }}
            color="purple" size="medium" variant="contained" onClick={onSubmit}>
            Save
          </LoadingButton>
        </Box>
      </Stack> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
      {orderGuidesToUpdate?.length > 0 && <ConfirmDialog
        action={() => { setOrderGuidesToUpdate([]); close(); }}
        cancelAction={() => setOrderGuidesToUpdate([])}
        dialogOnly
        noCancelButton
        actionButton={"Confirm"}
        color="primary"
        title="These order guides have this item with prices less than the vendor's cost price."
        content={<Box>
          {orderGuidesToUpdate.map((guide, index) => <Typography key={index} variant="subtitle1" textAlign="center">Order Guide: {guide}</Typography>)}
        </Box>}
      />}
    </DialogContent>
  </Dialog>;
}
